import { Dayjs } from "dayjs";
import { useState, useMemo, useEffect } from "react";
import Typography from "@mui/material/Typography";

import { AppointmentPicker } from "@/src/component/partial/AppointmentPicker";
import { FlowSpinner } from "@/src/component/partial/FlowSpinner";
import { ProviderHooks } from "@/src/hook";
import { TourLogic } from "@/src/logic";
import { StrapiComponentType, StrapiType, StrapiCollectionType } from "@/src/type";
import { Log } from "@/src/util";

import { ButtonLinkModule } from "../../../component/ButtonLinkModule";

import styles from "./Scheduler.module.scss";

interface SchedulerProps {
  propertyUnitDetails: StrapiComponentType.Section.PropertyUnitDetails;
  calendarConfig?: StrapiType.StrapiSingleWrapper<StrapiCollectionType.CalendarConfig>;
}

function Scheduler({ propertyUnitDetails, calendarConfig }: SchedulerProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [events, setEvents] = useState<any>([]);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<Dayjs | null | undefined>(null);
  const cms = ProviderHooks.useCMS();
  const calendarSettings = calendarConfig?.data?.attributes?.specificSettings?.[0];
  const integrationType = calendarSettings?.__component === "object.google-calendar" ? "google" : "yardi";
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getEvents = async () => {
      try {
        setIsLoading(true);
        if (!calendarSettings) {
          return;
        }
        const response = await TourLogic.getEvents(integrationType, calendarSettings);
        const _events = response.response as any[];
        if (Array.isArray(_events)) {
          setEvents(_events);
        }
      } catch (err) {
        Log.logToProvider(
          Log.LogLevel.ERROR,
          "DynamicPage/component/PropertyUnitDetails/component/Scheduler/Scheduler.tsx",
          "Get events",
          [err],
          "Error getting events"
        );
      }
      setIsLoading(false);
    };
    getEvents();
  }, [calendarSettings, integrationType]);

  const availableTimeSlots = useMemo(() => {
    return integrationType === "google"
      ? TourLogic.getAvailableTimeSlots(calendarConfig?.data?.attributes?.specificSettings?.[0], [...events])
      : TourLogic.getAvailableTimeSlotsYardi(events);
  }, [calendarConfig, events, integrationType]);

  function handleScheduleTour(date: Dayjs | null, time?: Dayjs | null) {
    setDate(date);
    setTime(time);
  }

  function handleClick() {
    if (!date || !time) {
      return;
    }

    const timestamp = encodeURIComponent(time.format());
    const link = cms.constructLink(propertyUnitDetails?.scheduledTourCta?.href ?? "");
    const url = link.includes("?") ? `${link}&timestamp=${timestamp}` : `${link}?timestamp=${timestamp}`;
    window.open(url, propertyUnitDetails?.scheduledTourCta?.target ?? "_self");
  }

  if (!calendarConfig || !availableTimeSlots) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography variant="body1">{propertyUnitDetails.scheduledTourTitle}</Typography>
      </div>
      <div className={styles.content}>
        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <FlowSpinner
              height={150}
              width={150}
            />
          </div>
        ) : (
          <AppointmentPicker
            availableTimeSlots={availableTimeSlots ?? []}
            daySlidesPerView={3}
            timeSlidesPerView={3}
            daysMarginBottom={true}
            onChange={handleScheduleTour}
          />
        )}
        {propertyUnitDetails.scheduledTourCta && (
          <ButtonLinkModule
            buttonLink={{
              ...propertyUnitDetails.scheduledTourCta,
              href: "#",
            }}
            forceButton={true}
            disabled={!date || !time}
            onClick={handleClick}
          />
        )}
      </div>
    </div>
  );
}

export default Scheduler;
